import {
  addCalendarDays,
  addCalendarMonths,
  isCalendarDay,
} from "./business-date.js";
import type {
  DateRange,
  PeriodPresetDefinition,
  PeriodSelection,
  RelativePeriodUnit,
} from "./types.js";

export const OPEN_PERIOD_ERROR =
  "Период не может включать сегодня и будущие дни. Последний доступный день — вчера.";

export class PeriodValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "PeriodValidationError";
  }
}

const RELATIVE_UNITS = new Set<RelativePeriodUnit>(["day", "week", "month"]);

export function parsePeriodPresetDefinition(
  value: unknown,
): PeriodPresetDefinition {
  if (!value || typeof value !== "object") {
    throw new PeriodValidationError("Некорректное определение периода");
  }
  const definition = value as { type?: unknown };
  if (definition.type === "absolute") {
    const from = readDay((value as { from?: unknown }).from, "from");
    const to = readDay((value as { to?: unknown }).to, "to");
    assertOrder(from, to);
    return { type: "absolute", from, to };
  }
  if (definition.type === "relative") {
    return {
      type: "relative",
      amount: readAmount((value as { amount?: unknown }).amount),
      unit: readUnit((value as { unit?: unknown }).unit),
    };
  }
  if (definition.type === "tracking") {
    return {
      type: "tracking",
      from: readDay((value as { from?: unknown }).from, "from"),
    };
  }
  throw new PeriodValidationError("Некорректный тип периода");
}

export function resolvePeriod(
  selection: PeriodSelection,
  anchorDay: string,
): DateRange {
  const anchor = readDay(anchorDay, "anchorDay");
  if (!selection || typeof selection !== "object") {
    throw new PeriodValidationError("Не указан период");
  }
  if (selection.type === "range") {
    return closeRange(selection.from, selection.to, anchor);
  }
  if (selection.type === "preset") {
    return resolvePreset(selection.preset, anchor);
  }
  throw new PeriodValidationError("Некорректный период");
}

export function assertClosedDateRange(
  from: string,
  to: string,
  anchorDay: string,
): DateRange {
  return resolvePeriod({ type: "range", from, to }, anchorDay);
}

function resolvePreset(
  preset: PeriodPresetDefinition,
  anchor: string,
): DateRange {
  const definition = parsePeriodPresetDefinition(preset);
  if (definition.type === "absolute") {
    return closeRange(definition.from, definition.to, anchor);
  }
  if (definition.type === "tracking") {
    if (definition.from > anchor)
      throw new PeriodValidationError(OPEN_PERIOD_ERROR);
    return { from: definition.from, to: anchor };
  }
  return {
    from: relativeStart(anchor, definition.amount, definition.unit),
    to: anchor,
  };
}

function closeRange(from: string, to: string, anchor: string): DateRange {
  const start = readDay(from, "from");
  const end = readDay(to, "to");
  assertOrder(start, end);
  if (start > anchor || end > anchor)
    throw new PeriodValidationError(OPEN_PERIOD_ERROR);
  return { from: start, to: end };
}

function relativeStart(
  anchor: string,
  amount: number,
  unit: RelativePeriodUnit,
): string {
  if (unit === "day") return addCalendarDays(anchor, -(amount - 1));
  if (unit === "week") return addCalendarDays(anchor, -(amount * 7 - 1));
  // Inclusive calendar month: the day after the date `amount` months before the anchor.
  return addCalendarDays(addCalendarMonths(anchor, -amount), 1);
}

function readDay(value: unknown, field: string): string {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    throw new PeriodValidationError(
      `Дата в поле ${field} должна быть в формате ГГГГ-ММ-ДД`,
    );
  }
  if (!isCalendarDay(value)) {
    throw new PeriodValidationError(`Дата в поле ${field} не существует`);
  }
  return value;
}

function assertOrder(from: string, to: string): void {
  if (from > to)
    throw new PeriodValidationError("Дата «С» не может быть позже даты «По»");
}

function readAmount(value: unknown): number {
  if (typeof value !== "number" || !Number.isInteger(value) || value <= 0) {
    throw new PeriodValidationError(
      "Количество должно быть целым числом больше нуля",
    );
  }
  return value;
}

function readUnit(value: unknown): RelativePeriodUnit {
  if (
    typeof value !== "string" ||
    !RELATIVE_UNITS.has(value as RelativePeriodUnit)
  ) {
    throw new PeriodValidationError("Некорректная единица периода");
  }
  return value as RelativePeriodUnit;
}
